import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  MapPin,
  Calendar,
  Users,
  Clock,
  CalendarDays,
  ClipboardList,
  Building2,
  ChevronRight,
  Video,
  Award,
  User,
  Heart,
} from 'lucide-react';
import CommentSection from '../components/comment/CommentSection';
import ShareButton from '../components/share/ShareButton';
import InstructorDetailsModal from '../components/InstructorDetailsModal';
import MainTabsNav from '../components/MainTabsNav';
import { useAuthStore } from '../stores/authStore';
import { useFavoritesStore } from '../stores/favoritesStore';
import type { Class, Instructor } from '../types/class';

export default function ClassDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  // 사업자 회원(교육 기관/기업 회원)은 수강 신청 불가 — admin은 허용
  const isBusinessMember = useAuthStore(
    (state) => state.user?.role === 'provider' || state.user?.role === 'employer',
  );
  // 기존 isProvider 변수명을 유지하되 의미는 "사업자 회원 = 신청 불가"
  const isProvider = isBusinessMember;
  const favorited = useFavoritesStore((state) => (id ? state.isFavorited(id) : false));
  const toggleFavorite = useFavoritesStore((state) => state.toggleFavorite);
  const fetchFavorites = useFavoritesStore((state) => state.fetchFavorites);
  const favoritesLoaded = useFavoritesStore((state) => state.loaded);
  const [favoritePending, setFavoritePending] = useState(false);
  const [classData, setClassData] = useState<Class | null>(null);
  const [providerName, setProviderName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enrolling, setEnrolling] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState(false);

  // 강사 모달 상태
  const [selectedInstructor, setSelectedInstructor] = useState<Instructor | null>(null);

  // 수업 데이터 조회
  useEffect(() => {
    const fetchClass = async () => {
      if (!id) {
        setError('수업 ID가 없습니다.');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/v1/classes/${id}`, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('수업 정보를 불러오는데 실패했습니다.');
        }

        const data = await response.json();
        setClassData(data.class);

        // Provider 이름 조회 (우수 인증 교육기관만 표시)
        const providerResponse = await fetch(`/api/v1/providers/${data.class.provider_id}`, {
          credentials: 'include',
        });
        if (providerResponse.ok) {
          const providerData = await providerResponse.json();
          setProviderName(providerData.provider?.company_name || null);
        } else {
          setProviderName(null);
        }

        // 수강 신청 여부 확인
        await checkEnrollmentStatus();
      } catch (err: any) {
        console.error('Failed to fetch class:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchClass();
  }, [id]);

  // 로그인 + 일반 회원일 때 관심 수업 목록 보장 (mount 시 1회)
  useEffect(() => {
    if (user && !isBusinessMember && !favoritesLoaded) {
      fetchFavorites();
    }
  }, [user, isBusinessMember, favoritesLoaded, fetchFavorites]);

  // 관심 수업 토글
  const handleFavoriteToggle = async () => {
    if (!id) return;
    if (!user) {
      const ok = confirm('로그인 후 관심 수업으로 저장할 수 있습니다. 로그인하시겠습니까?');
      if (ok) navigate('/login');
      return;
    }
    try {
      setFavoritePending(true);
      await toggleFavorite(id);
    } catch (err: any) {
      alert(err?.message || '관심 수업 처리에 실패했습니다.');
    } finally {
      setFavoritePending(false);
    }
  };

  // 수강 신청 여부 확인
  const checkEnrollmentStatus = async () => {
    try {
      const response = await fetch('/api/v1/enrollments', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        const enrolled = data.enrollments?.some(
          (enrollment: any) => enrollment.class_id === id && enrollment.status === 'ACTIVE'
        );
        setIsEnrolled(enrolled);
      }
    } catch (err) {
      console.error('Failed to check enrollment status:', err);
    }
  };

  // 상태 라벨 가져오기
  const getStatusLabel = (status: string) => {
    const labels: Record<string, { text: string; color: string }> = {
      DRAFT: { text: '임시저장', color: 'bg-gray-100 text-gray-700' },
      SCHEDULED: { text: '모집예정', color: 'bg-blue-100 text-blue-700' },
      RECRUITING: { text: '모집중', color: 'bg-green-100 text-green-700' },
      CLOSED: { text: '모집마감', color: 'bg-red-100 text-red-700' },
      IN_PROGRESS: { text: '진행중', color: 'bg-blue-100 text-blue-700' },
      COMPLETED: { text: '종료', color: 'bg-gray-100 text-gray-700' },
      CANCELLED: { text: '취소', color: 'bg-red-100 text-red-700' },
    };
    return labels[status] || labels.RECRUITING;
  };

  // 장소 타입 표시
  const getLocationText = (cls: Class) => {
    if (cls.location_type === 'ONLINE') {
      return '온라인';
    } else if (cls.location_type === 'OFFLINE') {
      return cls.location_address || '-';
    } else {
      return cls.location_address ? `온/오프라인 (${cls.location_address})` : '온/오프라인';
    }
  };

  // YouTube URL을 임베드 URL로 변환
  const getYouTubeEmbedUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);

      // youtu.be 형식
      if (urlObj.hostname === 'youtu.be') {
        const videoId = urlObj.pathname.slice(1);
        return `https://www.youtube.com/embed/${videoId}`;
      }

      // youtube.com/watch?v= 형식
      if (urlObj.hostname.includes('youtube.com')) {
        const videoId = urlObj.searchParams.get('v');
        if (videoId) {
          return `https://www.youtube.com/embed/${videoId}`;
        }
      }

      // 이미 embed URL인 경우
      if (url.includes('/embed/')) {
        return url;
      }

      return url;
    } catch (e) {
      return url;
    }
  };

  // 수강 신청 처리
  const handleEnroll = async () => {
    if (!id || !classData) return;

    // 이미 신청한 경우 내 수강 신청 관리로 이동
    if (isEnrolled) {
      navigate('/profile/enrollments');
      return;
    }

    if (isBusinessMember) {
      alert('교육 기관·기업 회원 계정으로는 수강 신청을 할 수 없습니다.');
      return;
    }

    try {
      setEnrolling(true);
      const response = await fetch(`/api/v1/classes/${id}/enroll`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || '수강 신청에 실패했습니다.');
      }

      alert('수강 신청이 완료되었습니다!');
      setIsEnrolled(true);
      // 수업 데이터 새로고침 (SPA 방식)
      const refreshRes = await fetch(`/api/v1/classes/${id}`, { credentials: 'include' });
      if (refreshRes.ok) {
        const refreshData = await refreshRes.json();
        setClassData(refreshData.class);
      }
    } catch (err: any) {
      console.error('Failed to enroll:', err);
      alert(err.message || '수강 신청에 실패했습니다.');
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !classData) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">수업을 찾을 수 없습니다</h2>
          <p className="text-gray-600 mb-6">{error || '존재하지 않는 수업입니다.'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            홈으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  const statusLabel = getStatusLabel(classData.status);

  // 할인 여부 및 할인율 계산
  const hasDiscount = classData.price > classData.discount_price;
  const discountRate = hasDiscount
    ? Math.round(((classData.price - classData.discount_price) / classData.price) * 100)
    : 0;
  const discountAmount = hasDiscount ? classData.price - classData.discount_price : 0;

  // 메타 태그용 데이터
  const pageUrl = `${window.location.origin}/classes/${id}`;
  const pageTitle = `${classData.title} - StampFit`;
  const plainDescription = classData.description
    ? classData.description.replace(/<[^>]*>/g, '')
    : '';
  const pageDescription = plainDescription
    ? plainDescription.slice(0, 150) + (plainDescription.length > 150 ? '...' : '')
    : '피트니스 지도자 자격 인증 및 구인구직 통합 플랫폼';
  const pageImage = classData.thumbnail_url || `${window.location.origin}/logo/stampfit-logo.png`;

  return (
    <>
      <Helmet>
        {/* 기본 메타 태그 */}
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />

        {/* Open Graph (Facebook, LinkedIn 등) */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={pageImage} />
        <meta property="og:site_name" content="StampFit" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />

        {/* 카카오톡 공유용 (Open Graph 활용) */}
        <meta property="og:locale" content="ko_KR" />
      </Helmet>

      <MainTabsNav />

      <div className="px-6 sm:px-10 lg:px-16 py-6">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
            <Link to="/" className="hover:text-primary">
              홈
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/" className="hover:text-primary">
              수강신청
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900">{classData.title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content - isolate로 sticky 사이드바와 합성 레이어 분리 */}
            <div className="lg:col-span-2 space-y-8 isolate">
              {/* Thumbnail */}
              {classData.thumbnail_url && (
                <img
                  src={classData.thumbnail_url}
                  alt={classData.title}
                  className="w-full aspect-video object-cover rounded-2xl"
                />
              )}

              {/* Title & Provider */}
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <span className={`text-sm font-medium px-3 py-1 rounded-full ${statusLabel.color}`}>
                    {statusLabel.text}
                  </span>
                  {classData.closing_soon && (
                    <span className="text-sm font-bold px-3 py-1 rounded-full bg-red-500 text-white">
                      마감 임박
                    </span>
                  )}
                  {classData.certificate_template_id && (
                    <span className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1 rounded-full bg-blue-100 text-blue-700">
                      <Award className="w-4 h-4" />
                      인증서 발급
                    </span>
                  )}
                </div>
                <h1 className="text-3xl font-bold text-gray-900">{classData.title}</h1>
                {providerName && (
                  <Link
                    to={`/providers/${classData.provider_id}`}
                    className="flex items-center gap-2 mt-3 text-gray-600 hover:text-primary transition-colors"
                  >
                    <Building2 className="w-5 h-5" />
                    <span className="font-medium">{providerName}</span>
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                )}

                {/* Instructor Info */}
                {classData.instructors && classData.instructors.length > 0 && (
                  <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 mt-2 text-gray-600">
                    <User className="w-5 h-5" />
                    <span className="font-medium">강사:</span>
                    {classData.instructors.map((instructor, idx) => (
                      <span key={instructor.id}>
                        <button
                          onClick={() => setSelectedInstructor(instructor)}
                          className="font-medium hover:text-primary transition-colors"
                        >
                          {instructor.name}
                        </button>
                        {idx < classData.instructors.length - 1 && <span className="text-gray-400">,</span>}
                      </span>
                    ))}
                  </div>
                )}

                {/* Share Button */}
                <div className="mt-4">
                  <ShareButton
                    url={`/classes/${classData.id}`}
                    title={classData.title}
                    description={classData.description?.replace(/<[^>]*>/g, '') || ''}
                  />
                </div>
              </div>

              {/* Description */}
              <div className="bg-white rounded-2xl shadow-soft p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">수업 소개</h2>
                {classData.description && classData.description.replace(/<[^>]*>/g, '').trim() ? (
                  <div
                    className="tiptap prose prose-gray max-w-none text-gray-700"
                    dangerouslySetInnerHTML={{ __html: classData.description }}
                  />
                ) : (
                  <p className="text-gray-700">-</p>
                )}
              </div>

              {/* Curriculum */}
              {classData.curriculum && (
                <div className="bg-white rounded-2xl shadow-soft p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">커리큘럼</h2>
                  <div
                    className="tiptap prose prose-gray max-w-none text-gray-700"
                    dangerouslySetInnerHTML={{ __html: classData.curriculum }}
                  />
                </div>
              )}

              {/* Comments */}
              <CommentSection classId={classData.id} providerId={classData.provider_id} />
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-soft p-6 sticky top-24">
                {/* Favorite (관심 수업) */}
                {!isBusinessMember && (
                  <div className="pb-6 border-b border-gray-100 mb-6">
                    <button
                      onClick={handleFavoriteToggle}
                      disabled={favoritePending}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-colors ${
                        favorited
                          ? 'border-accent bg-accent/5 hover:bg-accent/10'
                          : 'border-gray-200 hover:border-accent'
                      } ${favoritePending ? 'opacity-60 cursor-wait' : ''}`}
                    >
                      <Heart
                        className={`w-5 h-5 ${
                          favorited ? 'fill-accent text-accent' : 'text-gray-400'
                        }`}
                      />
                      <span className="font-medium text-sm">
                        {favorited ? '관심 수업에서 제거' : '관심 수업으로 저장'}
                      </span>
                    </button>
                  </div>
                )}

                {/* Price */}
                <div className="text-center pb-6 border-b border-gray-100">
                  <p className="text-sm text-gray-500">수강료</p>
                  {classData.discount_price > 0 ? (
                    <>
                      {hasDiscount && (
                        <div className="mt-1 flex items-center justify-center gap-2">
                          <p className="text-lg text-gray-400 line-through">
                            ₩{classData.price.toLocaleString()}
                          </p>
                          <span className="text-sm font-bold text-white bg-red-500 px-2 py-1 rounded">
                            {discountRate}% 할인
                          </span>
                        </div>
                      )}
                      <p className="text-3xl font-bold text-primary mt-2">
                        ₩{classData.discount_price.toLocaleString()}
                      </p>
                      {hasDiscount && (
                        <p className="text-sm text-red-500 font-medium mt-1">
                          {discountAmount.toLocaleString()}원 할인
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-3xl font-bold text-primary mt-2">-</p>
                  )}
                </div>

                {/* Info */}
                <div className="py-6 space-y-4 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <ClipboardList className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">모집 기간</p>
                      <p className="font-medium text-sm">
                        {/* 과거 수업(가격 0)은 모집 기간 미입력 → "-" */}
                        {classData.price === 0 ? '-' : new Date(classData.enrollment_start_date).toLocaleDateString('ko-KR')} ~{' '}
                        {classData.price === 0 ? '' : new Date(classData.enrollment_end_date).toLocaleDateString('ko-KR')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">수업 기간</p>
                      <p className="font-medium text-sm">
                        {/* 과거 수업(가격 0)은 시작일 미입력 → "-" */}
                        {classData.price === 0 ? '-' : new Date(classData.start_date).toLocaleDateString('ko-KR')} ~{' '}
                        {new Date(classData.end_date).toLocaleDateString('ko-KR')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <CalendarDays className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">수업 일정</p>
                      <p className="font-medium text-sm">{classData.schedule_info || '-'}</p>
                    </div>
                  </div>
                  {(classData.offline_hours > 0 || classData.online_hours > 0) && (
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-sm text-gray-500">총 수업 시간</p>
                        <p className="font-medium text-sm">
                          {classData.location_type === 'HYBRID'
                            ? `오프라인 ${classData.offline_hours}시간 / 온라인 ${classData.online_hours}시간`
                            : classData.location_type === 'ONLINE'
                              ? `${classData.online_hours}시간`
                              : `${classData.offline_hours}시간`
                          }
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">수업 장소</p>
                      <p className="font-medium text-sm">{getLocationText(classData)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">모집 인원</p>
                      <p className="font-medium text-sm">
                        {classData.current_count}/{classData.capacity}명
                        {classData.capacity - classData.current_count > 0 && (
                          <span className="text-accent ml-2">
                            ({classData.capacity - classData.current_count}자리 남음)
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div className="pt-6">
                  <button
                    onClick={handleEnroll}
                    className={`w-full font-bold px-6 py-4 rounded-lg shadow-soft transition-all ${
                      isEnrolled
                        ? 'bg-green-500 hover:bg-green-600 text-white hover:shadow-glow-accent cursor-pointer'
                        : !isProvider &&
                          classData.status === 'RECRUITING' &&
                          classData.current_count < classData.capacity &&
                          !enrolling
                        ? 'bg-accent hover:bg-accent-600 text-white hover:shadow-glow-accent'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                    disabled={
                      !isEnrolled &&
                      (isProvider ||
                        classData.status !== 'RECRUITING' ||
                        classData.current_count >= classData.capacity ||
                        enrolling)
                    }
                  >
                    {isEnrolled
                      ? '✓ 수강 신청 완료 (관리하기)'
                      : isBusinessMember
                      ? '사업자 계정 신청 불가'
                      : enrolling
                      ? '신청 중...'
                      : classData.current_count >= classData.capacity
                      ? '정원 마감'
                      : classData.status === 'SCHEDULED'
                      ? '모집 예정'
                      : classData.status === 'IN_PROGRESS'
                      ? '수업 진행중'
                      : classData.status === 'COMPLETED'
                      ? '종료된 수업'
                      : classData.status === 'CANCELLED'
                      ? '취소된 수업'
                      : classData.status !== 'RECRUITING'
                      ? '모집 종료'
                      : '수강 신청하기'}
                  </button>
                  {isBusinessMember && !isEnrolled && (
                    <p className="text-xs text-gray-500 text-center mt-2">
                      교육 기관·기업 회원 계정으로는 수강 신청을 할 수 없습니다.
                    </p>
                  )}
                </div>

                {/* YouTube Video */}
                {classData.youtube_url && (
                  <div className="pt-6 border-t border-gray-100 mt-6">
                    <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                      <Video className="w-5 h-5 text-primary" />
                      수업 소개 영상
                    </h2>
                    <div className="aspect-video rounded-lg overflow-hidden">
                      <iframe
                        src={getYouTubeEmbedUrl(classData.youtube_url)}
                        title="YouTube video"
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
      </div>

      {/* 강사 상세 모달 */}
      <InstructorDetailsModal
        isOpen={selectedInstructor !== null}
        instructor={selectedInstructor}
        onClose={() => setSelectedInstructor(null)}
      />
    </>
  );
}
